import React from "react";

type UseCommittedTextOptions<T> = {
  value: T | null;
  format: (value: T) => string;
  parse: (text: string) => T | null;
  /** Called with null when the text is cleared. Not called for text that fails to parse. */
  onCommit: (value: T | null) => void;
};

/**
 * Free-text editing that commits on blur / Enter rather than per keystroke. Invalid text stays
 * visible with `error` set instead of silently reverting, and the text follows `value` whenever
 * the formatted value changes (external updates, picker selections).
 */
export function useCommittedText<T>({ value, format, parse, onCommit }: UseCommittedTextOptions<T>) {
  const display = value === null ? "" : format(value);
  const [text, setText] = React.useState(display);
  const [error, setError] = React.useState(false);
  const dirtyRef = React.useRef(false);

  React.useEffect(() => {
    dirtyRef.current = false;
    setText(display);
    setError(false);
  }, [display]);

  const change = (nextText: string) => {
    dirtyRef.current = true;
    setText(nextText);
    setError(false);
  };

  /** Returns false when the text could not be parsed. */
  const commit = () => {
    if (!dirtyRef.current) return true;
    if (!text.trim()) {
      dirtyRef.current = false;
      setText("");
      onCommit(null);
      return true;
    }
    const parsed = parse(text);
    if (parsed === null) {
      setError(true);
      return false;
    }
    dirtyRef.current = false;
    setText(format(parsed));
    onCommit(parsed);
    return true;
  };

  /** Drops any pending edit, e.g. after a picker selection replaces the value. */
  const reset = () => {
    dirtyRef.current = false;
    setText(display);
    setError(false);
  };

  return { text, error, change, commit, reset };
}
